export interface ApplicationTaskChecks {
  firstName: boolean;
  preferredFirstName: boolean;
  lastName: boolean;
  phone: boolean;
  email: boolean;
  birthYear: boolean;
  birthMonth: boolean;
  birthDay: boolean;
  headshot: boolean;
  street: boolean;
  unit: boolean;
  city: boolean;
  province: boolean;
  postalCode: boolean;
  emergencyName: boolean;
  emergencyRelationship: boolean;
  emergencyPhone: boolean;
  emergencyEmail: boolean;
  income: boolean;
  photoId: boolean;
}

export interface ApplicationProgress {
  done: number;
  total: number;
  percent: number;
}

export function rentShareTotal(shares: Array<number | null | undefined>): number {
  const total = shares.reduce((sum, share) => sum + (share == null || !Number.isFinite(share) ? 0 : share), 0);
  return Math.round(total * 100) / 100;
}

export function rentSharesCoverRent(shares: Array<number | null | undefined>): boolean {
  return Math.round(rentShareTotal(shares) * 100) === 10000;
}

export interface AcademicChecks {
  year: boolean;
  faculty: boolean;
  program: boolean;
  graduation: boolean;
}

export interface BirthdayParts {
  year: string;
  month: string;
  day: string;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function phoneComplete(value: string): boolean {
  const digits = value.replace(/\D/g, '');
  if (digits.length === 10) return true;
  if (digits.length === 11 && digits.startsWith('1')) return true;
  return value.trim().startsWith('+') && digits.length >= 8 && digits.length <= 15;
}

export function emailComplete(value: string): boolean {
  const email = value.trim();
  return email.length > 0 && email.length <= 254 && EMAIL_PATTERN.test(email);
}

export function filled(value: string | null | undefined): boolean {
  return Boolean(value?.trim());
}

export function emptyBirthday(): BirthdayParts {
  return { year: '', month: '', day: '' };
}

export function birthdayPartsFromIso(value: string | null | undefined): BirthdayParts {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return emptyBirthday();
  const [year, month, day] = value.split('-');
  return { year, month, day };
}

export function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

export function birthdayIso(parts: BirthdayParts): string | null {
  if (!parts.year || !parts.month || !parts.day) return null;
  const year = Number(parts.year);
  const month = Number(parts.month);
  const day = Number(parts.day);
  if (!year || month < 1 || month > 12) return null;
  if (day < 1 || day > daysInMonth(year, month)) return null;
  return `${parts.year}-${parts.month}-${parts.day}`;
}

export function liveTaskChecks(
  person: {
    firstName: string;
    preferredFirstName: string;
    lastName: string;
    phone: string;
    email: string;
    address: string;
    unitNumber: string;
    city: string;
    province: string;
    postalCode: string;
    emergencyContactName: string;
    emergencyContactRelationship: string;
    emergencyContactPhone: string;
    emergencyContactEmail: string;
    hasHeadshot: boolean;
    hasPhotoId: boolean;
    incomeSources: Array<{ rentSharePercent: number | null }>;
  },
  birthday: BirthdayParts,
): ApplicationTaskChecks {
  return {
    firstName: filled(person.firstName),
    preferredFirstName: filled(person.preferredFirstName),
    lastName: filled(person.lastName),
    phone: phoneComplete(person.phone),
    email: emailComplete(person.email),
    birthYear: Boolean(birthday.year),
    birthMonth: Boolean(birthday.month),
    birthDay: Boolean(birthday.day) && birthdayIso(birthday) !== null,
    headshot: person.hasHeadshot,
    street: filled(person.address),
    unit: filled(person.unitNumber),
    city: filled(person.city),
    province: filled(person.province),
    postalCode: filled(person.postalCode),
    emergencyName: filled(person.emergencyContactName),
    emergencyRelationship: filled(person.emergencyContactRelationship),
    emergencyPhone: phoneComplete(person.emergencyContactPhone),
    emergencyEmail: emailComplete(person.emergencyContactEmail),
    income: rentSharesCoverRent(person.incomeSources.map((source) => source.rentSharePercent)),
    photoId: person.hasPhotoId,
  };
}

const COUNTED_TASKS: Array<keyof ApplicationTaskChecks> = [
  'firstName',
  'preferredFirstName',
  'lastName',
  'phone',
  'email',
  'birthYear',
  'birthMonth',
  'birthDay',
  'headshot',
  'street',
  'city',
  'province',
  'postalCode',
  'emergencyName',
  'emergencyRelationship',
  'emergencyPhone',
  'emergencyEmail',
  'income',
  'photoId',
];

export function academicChecksFromFields(person: {
  currentYearOfStudy: string;
  school: string;
  program: string;
  expectedGraduationTerm: string;
  expectedGraduationYear: string;
}): AcademicChecks {
  return {
    year: filled(person.currentYearOfStudy),
    faculty: filled(person.school),
    program: filled(person.program),
    graduation: filled(person.expectedGraduationTerm) && filled(person.expectedGraduationYear),
  };
}

export function applicationProgress(
  checks: ApplicationTaskChecks,
  homeAdded: boolean,
  academics: AcademicChecks,
): ApplicationProgress {
  const flags = [
    ...COUNTED_TASKS.map((key) => checks[key]),
    academics.year,
    academics.faculty,
    academics.program,
    academics.graduation,
    homeAdded,
  ];
  const done = flags.filter(Boolean).length;
  const total = flags.length;
  return {
    done,
    total,
    percent: total === 0 ? 0 : Math.round((done / total) * 100),
  };
}

/** Same total the Apply page shows as Application Progress: every person, including a linked home. */
export function applicationGroupProgress(input: {
  applicants: Array<{ taskChecks: ApplicationTaskChecks; academicChecks: AcademicChecks }>;
  homes: Array<{ linked: boolean }>;
}): { completed: number; total: number } {
  const homeAdded = input.homes.some((home) => home.linked);
  return input.applicants.reduce(
    (sum, applicant) => {
      const part = applicationProgress(applicant.taskChecks, homeAdded, applicant.academicChecks);
      return { completed: sum.completed + part.done, total: sum.total + part.total };
    },
    { completed: 0, total: 0 },
  );
}

export function contactSectionDone(checks: ApplicationTaskChecks): boolean {
  return checks.firstName && checks.preferredFirstName && checks.lastName && checks.phone && checks.email && checks.headshot;
}

export function basicSectionDone(checks: ApplicationTaskChecks, academics: AcademicChecks): boolean {
  return checks.birthYear && checks.birthMonth && checks.birthDay
    && academics.year && academics.faculty && academics.program && academics.graduation;
}

export function addressSectionDone(checks: ApplicationTaskChecks): boolean {
  return checks.street && checks.city && checks.province && checks.postalCode;
}

export function emergencySectionDone(checks: ApplicationTaskChecks): boolean {
  return checks.emergencyName && checks.emergencyRelationship && checks.emergencyPhone && checks.emergencyEmail;
}
